/**
 * ProcurementCartPage — Boltvezető Central Procurement Cart review (S-126-07).
 *
 * Read-only-first slice (7a): groups every ProcurementRequest in the current
 * tenant's cart by source (WORKSHEET / SALES / MANUAL / AUTO_MIN), with status
 * badges per Q-126-03 ratify (DRAFT / SUBMITTED / ORDERED / RECEIVED / CANCELLED).
 *
 * Slice 7b (next commit) adds per-row state-machine transition buttons.
 */

import {
  procurementActionErrorToastKey,
  type ProcurementRequest,
  type ProcurementRequestSource,
  type ProcurementRequestStatus,
} from '@/api/procurement';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import {
  useMarkProcurementGroupOrdered,
  useProcurementRequests,
  useSubmitProcurementCart,
  useUpdateProcurementRequest,
} from '@/hooks/use-procurement-requests';
import { useTranslation } from '@kgc/i18n';
import { useMemo, useState } from 'react';

const STATUS_BADGE: Record<ProcurementRequestStatus, string> = {
  DRAFT: 'bg-muted text-foreground',
  SUBMITTED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  ORDERED: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  RECEIVED: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  CANCELLED: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
};

const SOURCE_ORDER: ProcurementRequestSource[] = ['WORKSHEET', 'SALES', 'MANUAL', 'AUTO_MIN'];

/**
 * Phase 1 state-machine (Q-126-03 ratify Sprint 23 W0 D-2 + backend
 * `ALLOWED_TRANSITIONS` in procurement.service.ts).
 *   DRAFT     → SUBMITTED | CANCELLED
 *   SUBMITTED → ORDERED   | CANCELLED
 *   ORDERED   → RECEIVED  (CANCELLED Phase 2 — supplier-side comms)
 *   RECEIVED, CANCELLED → (terminal, no actions)
 */
const FORWARD_TRANSITION: Partial<Record<ProcurementRequestStatus, ProcurementRequestStatus>> = {
  DRAFT: 'SUBMITTED',
  SUBMITTED: 'ORDERED',
  ORDERED: 'RECEIVED',
};

function forwardActionLabelKey(status: ProcurementRequestStatus): string | null {
  switch (status) {
    case 'DRAFT':
      return 'procurement.actions.submit';
    case 'SUBMITTED':
      return 'procurement.actions.markOrdered';
    case 'ORDERED':
      return 'procurement.actions.markReceived';
    default:
      return null;
  }
}

const CANCELLABLE_STATUSES: ProcurementRequestStatus[] = ['DRAFT', 'SUBMITTED'];

function groupBySource(
  requests: ProcurementRequest[]
): Map<ProcurementRequestSource, ProcurementRequest[]> {
  const map = new Map<ProcurementRequestSource, ProcurementRequest[]>();
  for (const source of SOURCE_ORDER) {
    map.set(source, []);
  }
  for (const req of requests) {
    map.get(req.source)?.push(req);
  }
  return map;
}

export function ProcurementCartPage() {
  const { t } = useTranslation();
  // #1034 follow-up: ez az EGYETLEN szűrő NÉLKÜLI hívóhely — itt a teljes lista jön,
  // tehát a lapozás csonkítása is itt a legsúlyosabb (a mért 9943 sor ezen a képernyőn
  // jelenne meg). A `meta` a BE-oldal élesítése után érkezik; addig `undefined`.
  const { requests, meta, isTruncated, isLoading, error } = useProcurementRequests({});
  const updateMutation = useUpdateProcurementRequest();
  const submitMutation = useSubmitProcurementCart();
  const markGroupOrderedMutation = useMarkProcurementGroupOrdered();
  const grouped = useMemo(() => groupBySource(requests), [requests]);
  const [actionError, setActionError] = useState<string | null>(null);

  const draftCount = useMemo(() => requests.filter(r => r.status === 'DRAFT').length, [requests]);

  const handleSubmitCart = async (): Promise<void> => {
    if (draftCount === 0) return;
    if (!window.confirm(t('procurement.cart.confirmSubmit', { count: draftCount }))) return;
    setActionError(null);
    try {
      await submitMutation.mutateAsync();
    } catch (err) {
      // 68-kör 1️⃣ (adósság-zárás a #1753-hoz): a nyers `err.message` az `ApiError`
      // Error-leszármazottsága miatt MINDIG kiment, a lokalizált fallback holt kód
      // volt. Ugyanaz a classifier, amit a #1753 shippelt.
      setActionError(t(procurementActionErrorToastKey(err)));
    }
  };

  const handleMarkGroupOrdered = async (source: ProcurementRequestSource): Promise<void> => {
    const submittedInGroup = (grouped.get(source) ?? []).filter(r => r.status === 'SUBMITTED');
    if (submittedInGroup.length === 0) return;
    const supplierNotes = window.prompt(t('procurement.cart.supplierNotesPrompt')) ?? undefined;
    if (supplierNotes === undefined) return; // user pressed Cancel
    setActionError(null);
    try {
      await markGroupOrderedMutation.mutateAsync({
        source,
        ...(supplierNotes.trim() ? { supplierNotes: supplierNotes.trim() } : {}),
      });
    } catch (err) {
      // 68-kör 1️⃣ (adósság-zárás a #1753-hoz): a nyers `err.message` az `ApiError`
      // Error-leszármazottsága miatt MINDIG kiment, a lokalizált fallback holt kód
      // volt. Ugyanaz a classifier, amit a #1753 shippelt.
      setActionError(t(procurementActionErrorToastKey(err)));
    }
  };

  const handleTransition = async (
    req: ProcurementRequest,
    to: ProcurementRequestStatus
  ): Promise<void> => {
    setActionError(null);
    try {
      await updateMutation.mutateAsync({ id: req.id, input: { status: to } });
    } catch (err) {
      // 68-kör 1️⃣ (adósság-zárás a #1753-hoz): a nyers `err.message` az `ApiError`
      // Error-leszármazottsága miatt MINDIG kiment, a lokalizált fallback holt kód
      // volt. Ugyanaz a classifier, amit a #1753 shippelt.
      setActionError(t(procurementActionErrorToastKey(err), { from: req.status, to }));
    }
  };

  const handleCancel = async (req: ProcurementRequest): Promise<void> => {
    if (!window.confirm(t('procurement.actions.confirmCancel'))) return;
    await handleTransition(req, 'CANCELLED');
  };

  return (
    <main className="mx-auto max-w-6xl px-4 py-6">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('procurement.cart.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('procurement.cart.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          {/* #1034: csonkított listánál a `requests.length` HAMIS összeg lenne — egy
              teljesnek látszó „20 igény" a valós 9943 helyett. Ilyenkor a valós totalt
              írjuk ki, és külön jelezzük, hogy csak egy részlet látszik. */}
          <div className="text-sm text-muted-foreground" data-testid="procurement-cart-total">
            {isTruncated && meta
              ? t('procurement.listTruncated', {
                  shown: String(requests.length),
                  total: String(meta.total),
                })
              : t('procurement.cart.totalCount', { count: requests.length })}
          </div>
          <Button
            size="sm"
            disabled={draftCount === 0 || submitMutation.isPending}
            onClick={() => void handleSubmitCart()}
            data-testid="procurement-cart-submit-button"
          >
            {submitMutation.isPending
              ? t('common.saving')
              : t('procurement.cart.submitButton', { count: draftCount })}
          </Button>
        </div>
      </header>

      {actionError && (
        <p
          className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
          role="alert"
          data-testid="procurement-cart-action-error"
        >
          {actionError}
        </p>
      )}
      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : error ? (
        <p
          className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
          role="alert"
        >
          {t('service.errorPrefix', { message: error })}
        </p>
      ) : requests.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            {t('procurement.cart.empty')}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="procurement-cart-list">
          {SOURCE_ORDER.map(source => {
            const items = grouped.get(source) ?? [];
            if (items.length === 0) return null;
            return (
              <Card key={source} data-testid={`procurement-source-group-${source}`}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between gap-2 text-base">
                    <span>{t(`procurement.cart.sourceLabel.${source}`)}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-normal text-muted-foreground">
                        {items.length}
                      </span>
                      {items.some(r => r.status === 'SUBMITTED') && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={markGroupOrderedMutation.isPending}
                          onClick={() => void handleMarkGroupOrdered(source)}
                          data-testid={`procurement-mark-group-ordered-${source}`}
                        >
                          {t('procurement.cart.markGroupOrderedButton')}
                        </Button>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                    {items.map(req => {
                      const forwardTo = FORWARD_TRANSITION[req.status];
                      const forwardLabelKey = forwardActionLabelKey(req.status);
                      const canCancel = CANCELLABLE_STATUSES.includes(req.status);
                      return (
                        <li
                          key={req.id}
                          className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm"
                          data-testid={`procurement-cart-row-${req.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 dark:text-white">
                              {t('procurement.cart.productIdShort')}: {req.productId.slice(0, 8)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {t('procurement.cart.quantity')}: {req.quantity}
                              {req.dayBucket ? ` · ${req.dayBucket}` : ''}
                              {req.notes ? ` · ${req.notes}` : ''}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[req.status]}`}
                              data-testid={`procurement-cart-status-${req.id}`}
                            >
                              {t(`procurement.status.${req.status}`)}
                            </span>
                            {forwardTo && forwardLabelKey && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={updateMutation.isPending}
                                onClick={() => void handleTransition(req, forwardTo)}
                                data-testid={`procurement-cart-forward-${req.id}`}
                              >
                                {t(forwardLabelKey)}
                              </Button>
                            )}
                            {canCancel && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={updateMutation.isPending}
                                onClick={() => void handleCancel(req)}
                                data-testid={`procurement-cart-cancel-${req.id}`}
                                className="text-red-600 dark:text-red-400"
                              >
                                {t('procurement.actions.cancel')}
                              </Button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </main>
  );
}
